import React from 'react';

interface NoScriptFallbackProps {
  className?: string;
}

export const NoScriptFallback: React.FC<NoScriptFallbackProps> = ({ 
  className = '' 
}) => {
  return (
    <noscript>
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center p-4 ${className}`}>
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Warning Icon */}
            <div className="w-16 h-16 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              JavaScript Required
            </h1>
            
            <p className="text-lg text-gray-600 mb-6">
              This bowling league statistics platform requires JavaScript to function properly. 
              Please enable JavaScript in your browser to access all features.
            </p>

            {/* Feature list */}
            <div className="text-left mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                What you'll get with JavaScript enabled:
              </h2>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Interactive performance charts and analytics
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Real-time bowling statistics tracking
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Player comparison and league rankings
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Lane performance analysis
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Mobile-responsive interface
                </li>
              </ul>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                How to enable JavaScript:
              </h3>
              <div className="text-sm text-blue-800 space-y-2 text-left">
                <div>
                  <strong>Chrome:</strong> Settings → Privacy and security → Site Settings → JavaScript → Allowed
                </div>
                <div>
                  <strong>Firefox:</strong> Type "about:config" → search "javascript.enabled" → set to true
                </div>
                <div>
                  <strong>Safari:</strong> Preferences → Security → Enable JavaScript
                </div>
                <div>
                  <strong>Edge:</strong> Settings → Site permissions → JavaScript → Allowed
                </div>
              </div>
            </div>

            {/* Alternative access */}
            <div className="border-t pt-6">
              <p className="text-gray-600 mb-4">
                If you cannot enable JavaScript, you can still:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/contact"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Contact Support
                </a>
                <a
                  href="mailto:support@bowling.donaghy.zip"
                  className="inline-block bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Email Us
                </a>
              </div>
            </div>
          </div>

          {/* Footer info */}
          <div className="mt-6 text-center text-gray-500">
            <p className="text-sm">
              This site uses modern web technologies to provide the best bowling statistics experience.
            </p>
          </div>
        </div>
      </div>
    </noscript>
  );
};

// Component for basic HTML fallback content
export const BasicHTMLFallback: React.FC = () => {
  return (
    <div style={{ display: 'none' }}>
      <noscript>
        <style>
          {`
            .js-only { display: none !important; }
            .no-js-show { display: block !important; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          `}
        </style>
        <div className="no-js-show" style={{ 
          minHeight: '100vh', 
          backgroundColor: '#f9fafb', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '1rem' 
        }}>
          <div style={{ 
            maxWidth: '32rem', 
            width: '100%', 
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            padding: '2rem'
          }}>
            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '1rem' 
            }}>
              Bowling League Statistics
            </h1>
            <p style={{ 
              fontSize: '1.125rem', 
              color: '#6b7280', 
              marginBottom: '2rem' 
            }}>
              Track your bowling performance, analyze your games, and improve your scores with our comprehensive statistics platform.
            </p>
            <p style={{ 
              color: '#ef4444', 
              marginBottom: '1.5rem' 
            }}>
              JavaScript is required for full functionality.
            </p>
            <div>
              <a 
                href="/login" 
                style={{ 
                  display: 'inline-block',
                  backgroundColor: '#2563eb', 
                  color: 'white', 
                  padding: '0.75rem 1.5rem', 
                  borderRadius: '0.5rem', 
                  textDecoration: 'none',
                  fontWeight: '500',
                  marginRight: '0.5rem'
                }}
              >
                Login
              </a>
              <a 
                href="/contact" 
                style={{ 
                  display: 'inline-block',
                  backgroundColor: '#f3f4f6', 
                  color: '#374151', 
                  padding: '0.75rem 1.5rem', 
                  borderRadius: '0.5rem', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </noscript>
    </div>
  );
};

export default NoScriptFallback;